import { useCallback, useEffect, useMemo, useState } from "react";

/* =========================================================
   CONFIG  (baaki darshan pages wali same values)
========================================================= */
const API_BASE = import.meta?.env?.VITE_API_URL || "https://bmtvendorvendorfinal.onrender.com";

const DARSHAN_LIST_URLS = [
  `${API_BASE}/api/vendor/darshans/my-darshans`,
  `${API_BASE}/api/vendor/darshans/my`,
  `${API_BASE}/api/vendor/darshans`,
];
const TYPES_URL = `${API_BASE}/api/vendor/darshan-types/my-types`;
const SLOTS_URL = `${API_BASE}/api/vendor/darshan-slots/my-slots`;
const AV_URL = `${API_BASE}/api/vendor/darshan-slot-availability/my-availability`;
const BOOKINGS_URL = `${API_BASE}/api/darshan-bookings/vendor`;

/* =========================================================
   HELPERS
========================================================= */
const getToken = () => {
  const v = localStorage.getItem("vendorToken");
  return v && v !== "null" && v !== "undefined" ? v : "";
};

async function get(url) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}`, Accept: "application/json" } });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const err = new Error(json.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}

const toList = (json) => {
  if (Array.isArray(json)) return json;
  const d = json?.data ?? json?.darshans ?? json?.items;
  return Array.isArray(d) ? d : [];
};

// local YYYY-MM-DD
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayKey = () => ymd(new Date());
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};

// Backend availability date UTC midnight par store karta hai
const dateKey = (iso) => String(iso || "").slice(0, 10);

const money = (n, c = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: c || "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);
  } catch {
    return `₹${Number(n) || 0}`;
  }
};

const fmtTime = (t) => {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return t || "--";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const fmtDayShort = (key) =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", timeZone: "UTC" });

const darshanLabel = (d) => d?.templeName || d?.name || d?.title || "Temple";
const isLive = (b) => !["cancelled", "refunded"].includes(b.bookingStatus);

const availState = (a) => {
  if (a.isBlocked) return "blocked";
  if (a.isSoldOut || (a.totalCapacity > 0 && a.availableCapacity <= 0)) return "soldout";
  if (!a.isActive || !a.isBookable) return "paused";
  return "open";
};

/* =========================================================
   COMPONENT
========================================================= */
export default function DarshanDashboard({ onNavigate = () => {} }) {
  const [darshans, setDarshans] = useState([]);
  const [types, setTypes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [avail, setAvail] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState({ list: [], total: 0 });
  const [todayBookings, setTodayBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    const today = todayKey();
    const to = ymd(addDays(6));

    // darshan list ke liye multiple endpoints try hote hain (baaki pages jaisa)
    const loadDarshans = async () => {
      for (const url of DARSHAN_LIST_URLS) {
        try {
          return toList(await get(url));
        } catch (e) {
          if (e.status === 401) throw e;
        }
      }
      return [];
    };

    const results = await Promise.allSettled([
      loadDarshans(),
      get(TYPES_URL),
      get(SLOTS_URL),
      get(`${AV_URL}?from=${today}&to=${to}`),
      get(`${BOOKINGS_URL}/summary`),
      get(`${BOOKINGS_URL}?status=pending&limit=5&page=1`),
      get(`${BOOKINGS_URL}?bookingDate=${today}&limit=100&page=1`),
    ]);

    const [d, t, s, a, sm, pd, td] = results;
    setDarshans(d.status === "fulfilled" ? d.value : []);
    setTypes(t.status === "fulfilled" ? toList(t.value) : []);
    setSlots(s.status === "fulfilled" ? toList(s.value) : []);
    setAvail(a.status === "fulfilled" ? toList(a.value) : []);
    setSummary(sm.status === "fulfilled" ? sm.value.data || null : null);
    setPending(
      pd.status === "fulfilled"
        ? { list: toList(pd.value), total: pd.value.total ?? toList(pd.value).length }
        : { list: [], total: 0 }
    );
    setTodayBookings(td.status === "fulfilled" ? toList(td.value) : []);

    const failed = results.find((r) => r.status === "rejected");
    if (failed) {
      setError(
        failed.reason?.status === 401
          ? "Session expired. Please login again."
          : failed.reason?.message || "Kuch data load nahi ho paya."
      );
    }

    setUpdatedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- derived ---------- */
  const d = useMemo(() => {
    const today = todayKey();

    const visits = todayBookings.filter(isLive);
    const visitPersons = visits.reduce((s, b) => s + (b.totalPersons || 0), 0);
    const checkedIn = visits.filter((b) => b.entryStatus === "checked_in").length;

    const openAvail = avail.filter((a) => availState(a) === "open");
    const seatsOpen = openAvail.reduce((s, a) => s + (a.availableCapacity || 0), 0);

    // next 7 days chart
    const days = Array.from({ length: 7 }, (_, i) => {
      const key = ymd(addDays(i));
      const rows = avail.filter((a) => dateKey(a.date) === key);
      const total = rows.reduce((s, a) => s + (a.totalCapacity || 0), 0);
      const booked = rows.reduce((s, a) => s + (a.bookedCapacity || 0), 0);
      return {
        key,
        slots: rows.length,
        total,
        booked,
        pct: total ? Math.min(100, Math.round((booked / total) * 100)) : 0,
        blocked: rows.length > 0 && rows.every((a) => a.isBlocked),
      };
    });

    const todaySchedule = avail
      .filter((a) => dateKey(a.date) === today)
      .sort((a, b) => String(a.slotId?.startTime || "").localeCompare(String(b.slotId?.startTime || "")));

    const rejected = darshans.filter((x) => x.status === "rejected");
    const inReview = darshans.filter((x) => x.status === "pending");

    return { visits, visitPersons, checkedIn, seatsOpen, days, todaySchedule, rejected, inReview };
  }, [todayBookings, avail, darshans]);

  const futureAvailCount = avail.filter((a) => dateKey(a.date) >= todayKey()).length;

  const checklist = [
    { done: darshans.length > 0, label: "Temple / darshan add karo", go: "darshan_add" },
    { done: darshans.some((x) => x.status === "approved"), label: "Listing approval ka wait / approve karwao", go: "darshan_add" },
    { done: types.length > 0, label: "Darshan types banao (General, VIP…)", go: "darshan_types" },
    { done: slots.length > 0, label: "Time slots add karo", go: "darshan_slots" },
    { done: futureAvailCount > 0, label: "Aane wali dates ki availability kholo", go: "darshan_availability" },
    { done: (summary?.totalBookings || 0) > 0, label: "Pehli booking receive karo", go: "darshan_bookings" },
  ];
  const doneCount = checklist.filter((c) => c.done).length;

  const metrics = [
    {
      title: "Today's Visits",
      value: d.visits.length,
      sub: `${d.visitPersons} DEVOTEES · ${d.checkedIn} CHECKED IN`,
      go: "darshan_bookings",
    },
    {
      title: "Pending Confirmation",
      value: pending.total,
      sub: "WAITING FOR YOUR ACTION",
      go: "darshan_bookings",
    },
    {
      title: "Paid Revenue",
      value: money(summary?.paidAmount),
      sub: `${summary?.totalBookings ?? 0} TOTAL BOOKINGS`,
      go: "darshan_bookings",
    },
    {
      title: "Seats Open (7 days)",
      value: d.seatsOpen,
      sub: `${avail.filter((a) => availState(a) === "open").length} OPEN SLOT-DATES`,
      go: "darshan_availability",
    },
  ];

  const stat = (k) => summary?.[k] ?? 0;

  return (
    <div className="dsh">
      <style>{CSS}</style>

      <section className="dsh-head">
        <div>
          <h1>Performance Overview</h1>
          <p>
            {updatedAt
              ? `Last updated ${updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
              : "Loading…"}
          </p>
        </div>
        <button className="dsh-btn" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      {error && <div className="dsh-alert bad">{error}</div>}

      {d.rejected.length > 0 && (
        <div className="dsh-alert warn">
          <div>
            <b>{d.rejected.length} listing rejected.</b>{" "}
            {d.rejected.map((x) => `${darshanLabel(x)}${x.rejectionReason ? ` — ${x.rejectionReason}` : ""}`).join(" | ")}
          </div>
          <button className="dsh-link" onClick={() => onNavigate("darshan_add")}>Fix now</button>
        </div>
      )}
      {d.inReview.length > 0 && d.rejected.length === 0 && (
        <div className="dsh-alert info">
          {d.inReview.length} listing review mein hai. Approve hote hi bookings start ho jayengi.
        </div>
      )}

      <section className="dsh-banner">
        <div>
          <h2>Darshan Analytics</h2>
          <p>
            Pending <b>{stat("pendingBookings")}</b> · Confirmed <b>{stat("confirmedBookings")}</b> · Completed{" "}
            <b>{stat("completedBookings")}</b> · Cancelled <b>{stat("cancelledBookings")}</b> · Total devotees{" "}
            <b>{stat("totalPersons")}</b>
          </p>
        </div>
        <button className="dsh-btn light" onClick={() => onNavigate("darshan_bookings")}>View Bookings</button>
      </section>

      <section className="dsh-metrics">
        {metrics.map((m) => (
          <article className="dsh-metric" key={m.title}>
            <h3>{m.title}</h3>
            <div className="dsh-value">{loading ? "…" : m.value}</div>
            <p>{m.sub}</p>
            <button onClick={() => onNavigate(m.go)}>View More</button>
          </article>
        ))}
      </section>

      <section className="dsh-grid">
        {/* Next 7 days */}
        <article className="dsh-card">
          <div className="dsh-card-head">
            <div>
              <h3>Next 7 days – seats booked</h3>
              <p>Opened slot-dates ke hisaab se</p>
            </div>
            <button className="dsh-link" onClick={() => onNavigate("darshan_availability")}>Manage</button>
          </div>
          <div className="dsh-chart">
            {d.days.map((day) => (
              <div className="dsh-col" key={day.key} title={day.slots ? `${day.booked}/${day.total} seats booked` : "No availability opened"}>
                <span className="dsh-col-val">{day.slots ? `${day.booked}/${day.total}` : "—"}</span>
                <div className="dsh-track">
                  <i
                    className={day.blocked ? "blocked" : day.pct >= 100 ? "full" : ""}
                    style={{ height: `${day.slots ? Math.max(day.pct, 4) : 0}%` }}
                  />
                </div>
                <span className="dsh-col-label">{fmtDayShort(day.key)}</span>
                {day.blocked && <span className="dsh-col-tag">Blocked</span>}
              </div>
            ))}
          </div>
          {d.days.every((x) => !x.slots) && !loading && (
            <Empty text="Aane wale 7 dino ki koi availability open nahi hai." action="Add availability" onClick={() => onNavigate("darshan_availability")} />
          )}
        </article>

        {/* Setup progress */}
        <article className="dsh-card">
          <div className="dsh-card-head">
            <div>
              <h3>Setup Progress</h3>
              <p>{doneCount} of {checklist.length} steps done</p>
            </div>
            <span className="dsh-badge">{darshans.length} temple(s)</span>
          </div>
          <div className="dsh-progress"><i style={{ width: `${(doneCount / checklist.length) * 100}%` }} /></div>
          <div className="dsh-list">
            {checklist.map((c) => (
              <button key={c.label} className={c.done ? "done" : ""} onClick={() => onNavigate(c.go)}>
                <span className="dsh-tick">{c.done ? "✓" : ""}</span>
                <span className="dsh-list-text">{c.label}</span>
                <b>›</b>
              </button>
            ))}
          </div>
          <div className="dsh-chips">
            <span>Types {types.length}</span>
            <span>Slots {slots.length}</span>
            <span>Approved {darshans.filter((x) => x.status === "approved").length}</span>
            <span>Draft {darshans.filter((x) => x.status === "draft").length}</span>
          </div>
        </article>

        {/* Today's schedule */}
        <article className="dsh-card">
          <div className="dsh-card-head">
            <div>
              <h3>Today's Slots</h3>
              <p>{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}</p>
            </div>
            <button className="dsh-link" onClick={() => onNavigate("darshan_slots")}>Slots</button>
          </div>
          {d.todaySchedule.length === 0 ? (
            <Empty text="Aaj ke liye koi slot open nahi hai." action="Open a date" onClick={() => onNavigate("darshan_availability")} />
          ) : (
            <div className="dsh-rows">
              {d.todaySchedule.map((a) => {
                const st = availState(a);
                const pct = a.totalCapacity ? Math.min(100, Math.round(((a.bookedCapacity || 0) / a.totalCapacity) * 100)) : 0;
                return (
                  <div className="dsh-row" key={a._id}>
                    <div className="dsh-time">
                      <b>{fmtTime(a.slotId?.startTime)}</b>
                      <small>{fmtTime(a.slotId?.endTime)}</small>
                    </div>
                    <div className="dsh-row-main">
                      <strong>{a.slotId?.name || "Slot"}</strong>
                      <small>{a.darshanTypeId?.name || "-"} · {darshanLabel(a.darshanId)}</small>
                      <div className="dsh-progress sm"><i className={pct >= 100 ? "full" : ""} style={{ width: `${pct}%` }} /></div>
                      <small>{a.bookedCapacity || 0}/{a.totalCapacity} booked</small>
                    </div>
                    <span className={`dsh-pill ${st}`}>{st === "soldout" ? "Sold out" : st}</span>
                  </div>
                );
              })}
            </div>
          )}
        </article>

        {/* Needs action */}
        <article className="dsh-card">
          <div className="dsh-card-head">
            <div>
              <h3>Needs Your Action</h3>
              <p>Confirmation ka wait kar rahi bookings</p>
            </div>
            <span className="dsh-badge warn">{pending.total}</span>
          </div>
          {pending.list.length === 0 ? (
            <Empty text="Koi pending booking nahi hai." />
          ) : (
            <div className="dsh-rows">
              {pending.list.map((b) => (
                <div className="dsh-row" key={b._id}>
                  <div className="dsh-row-main">
                    <strong>{b.customerName || "Devotee"}</strong>
                    <small>
                      {b.darshanTypeId?.name || b.darshanId?.templeName || "Darshan"} ·{" "}
                      {b.bookingDate ? new Date(b.bookingDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" }) : "-"}{" "}
                      · {b.totalPersons} pax
                    </small>
                  </div>
                  <div className="dsh-right">
                    <strong>{money(b.totalAmount, b.currency)}</strong>
                    <span className={`dsh-pill ${b.paymentStatus === "paid" ? "open" : "paused"}`}>{b.paymentStatus}</span>
                  </div>
                </div>
              ))}
              <button className="dsh-btn" onClick={() => onNavigate("darshan_bookings")}>Review all bookings</button>
            </div>
          )}
        </article>
      </section>

      <section className="dsh-quick">
        {[
          ["My Darshans", "darshan_add"],
          ["Darshan Types", "darshan_types"],
          ["Darshan Slots", "darshan_slots"],
          ["Slot Availability", "darshan_availability"],
          ["Bookings", "darshan_bookings"],
        ].map(([label, go]) => (
          <button key={go} onClick={() => onNavigate(go)}>{label}<b>›</b></button>
        ))}
      </section>
    </div>
  );
}

function Empty({ text, action, onClick }) {
  return (
    <div className="dsh-empty">
      <p>{text}</p>
      {action && <button className="dsh-btn" onClick={onClick}>{action}</button>}
    </div>
  );
}

/* =========================================================
   STYLES (prefix dsh-)
========================================================= */
const CSS = `
.dsh{--ink:#241c1a;--muted:#6f6560;--line:#e7dfd6;--paper:#fffdf9;--soft:#f3eee7;--maroon:#7a1f2b;--maroon-d:#5d1620;--marigold:#e9a21b;--ok:#2f7d4f;--err:#b3261e;color:var(--ink);font-size:14px;line-height:1.5;display:flex;flex-direction:column;gap:18px}
.dsh *{box-sizing:border-box}
.dsh h1,.dsh h2,.dsh h3,.dsh p{margin:0}
.dsh-head{display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap}
.dsh-head h1{font-size:28px}
.dsh-head p{color:var(--muted);margin-top:4px}
.dsh-btn{background:#fff;color:var(--ink);border:1px solid var(--line);border-radius:8px;padding:9px 16px;font:inherit;font-weight:600;cursor:pointer}
.dsh-btn:hover:not(:disabled){border-color:#cfc3b6}
.dsh-btn:disabled{opacity:.55;cursor:not-allowed}
.dsh-btn.light{border-color:transparent}
.dsh-btn:focus-visible,.dsh-link:focus-visible,.dsh button:focus-visible{outline:2px solid var(--maroon);outline-offset:2px}
.dsh-link{background:none;border:0;color:var(--maroon);font:inherit;font-weight:600;cursor:pointer;text-decoration:underline}

.dsh-alert{display:flex;justify-content:space-between;align-items:center;gap:12px;border-radius:10px;padding:10px 14px;font-size:13.5px}
.dsh-alert.bad{background:#fdecea;color:#7d1a14;border:1px solid #f3c1bc}
.dsh-alert.warn{background:#fdf1d6;color:#7a4c00;border:1px solid #f1d9a2}
.dsh-alert.info{background:#e9f0fb;color:#1d3f7a;border:1px solid #c6d6f1}

.dsh-banner{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;background:linear-gradient(135deg,var(--maroon-d),var(--maroon));color:#fff;border-radius:14px;padding:20px 24px}
.dsh-banner h2{font-size:20px}
.dsh-banner p{margin-top:6px;opacity:.92}

.dsh-metrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:14px}
.dsh-metric{background:var(--paper);border:1px solid var(--line);border-radius:12px;padding:18px;display:flex;flex-direction:column;gap:6px}
.dsh-metric h3{font-size:14px;color:var(--muted);font-weight:600}
.dsh-value{font-size:32px;font-weight:700}
.dsh-metric p{font-size:11px;letter-spacing:.04em;color:var(--muted)}
.dsh-metric button{align-self:flex-start;margin-top:6px;background:none;border:0;padding:0;color:var(--maroon);font:inherit;font-weight:600;cursor:pointer}

.dsh-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
@media(max-width:900px){.dsh-grid{grid-template-columns:1fr}}
.dsh-card{background:var(--paper);border:1px solid var(--line);border-radius:12px;padding:18px;display:flex;flex-direction:column;gap:14px;min-width:0}
.dsh-card-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.dsh-card-head h3{font-size:17px}
.dsh-card-head p{font-size:13px;color:var(--muted);margin-top:3px}
.dsh-badge{background:#f6e7e9;color:var(--maroon);font-size:12px;font-weight:700;padding:4px 10px;border-radius:99px;white-space:nowrap}
.dsh-badge.warn{background:#fdf1d6;color:#8a5a00}

.dsh-chart{display:flex;align-items:flex-end;gap:10px;height:200px}
.dsh-col{flex:1;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;gap:4px;min-width:0}
.dsh-col-val{font-size:10.5px;color:var(--muted);white-space:nowrap}
.dsh-track{width:100%;max-width:44px;flex:1;background:var(--soft);border-radius:6px;display:flex;align-items:flex-end;overflow:hidden}
.dsh-track i{display:block;width:100%;background:var(--ok);border-radius:6px 6px 0 0}
.dsh-track i.full{background:var(--err)}
.dsh-track i.blocked{background:#bdb3a8}
.dsh-col-label{font-size:12px;color:var(--muted);white-space:nowrap}
.dsh-col-tag{font-size:10px;color:var(--err)}

.dsh-progress{height:8px;background:var(--soft);border-radius:99px;overflow:hidden}
.dsh-progress.sm{height:5px;margin:6px 0 3px}
.dsh-progress i{display:block;height:100%;background:var(--maroon);border-radius:99px}
.dsh-progress i.full{background:var(--err)}
.dsh-list{display:flex;flex-direction:column;gap:6px}
.dsh-list button{display:flex;align-items:center;gap:10px;text-align:left;background:var(--soft);border:0;border-radius:8px;padding:10px 12px;font:inherit;color:var(--ink);cursor:pointer}
.dsh-list-text{flex:1}
.dsh-list button.done{color:var(--muted)}
.dsh-list button.done .dsh-list-text{text-decoration:line-through}
.dsh-tick{width:20px;height:20px;border-radius:50%;border:1.5px solid var(--line);background:#fff;display:grid;place-items:center;font-size:12px;color:#fff;flex:none}
.dsh-list .done .dsh-tick{background:var(--ok);border-color:var(--ok)}
.dsh-chips{display:flex;gap:6px;flex-wrap:wrap}
.dsh-chips span{font-size:12px;border:1px solid var(--line);border-radius:6px;padding:2px 8px;color:var(--muted)}

.dsh-rows{display:flex;flex-direction:column;gap:12px}
.dsh-row{display:flex;align-items:center;gap:12px}
.dsh-row-main{flex:1;min-width:0}
.dsh-row-main strong{display:block;font-size:15px}
.dsh-row-main small{display:block;color:var(--muted);font-size:12px}
.dsh-time{min-width:70px;border-right:2px solid var(--marigold);padding-right:10px}
.dsh-time b{display:block;font-size:14px}
.dsh-time small{color:var(--muted);font-size:12px}
.dsh-right{text-align:right;display:flex;flex-direction:column;gap:4px;align-items:flex-end}
.dsh-pill{font-size:11px;font-weight:700;padding:3px 9px;border-radius:99px;background:var(--soft);color:var(--muted);text-transform:capitalize;white-space:nowrap}
.dsh-pill.open{background:#e3f3e9;color:var(--ok)}
.dsh-pill.paused{background:#fdf1d6;color:#8a5a00}
.dsh-pill.soldout,.dsh-pill.blocked{background:#fdecea;color:var(--err)}
.dsh-empty{text-align:center;padding:22px 10px;color:var(--muted);display:flex;flex-direction:column;gap:10px;align-items:center}

.dsh-quick{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px}
.dsh-quick button{display:flex;justify-content:space-between;align-items:center;background:var(--paper);border:1px solid var(--line);border-radius:10px;padding:14px 16px;font:inherit;font-weight:600;color:var(--ink);cursor:pointer}
.dsh-quick button:hover{border-color:var(--maroon);color:var(--maroon)}
`;